/**
 * Query-state codec (Req 8.12, Property 15).
 *
 * The list view's full query — free-text search term, attribute/capability
 * filters, and sort — is the state preserved across detail navigation. These
 * two functions serialize a {@link QueryState} to a compact, URL-safe string
 * and parse it back, such that `decodeQueryState(encodeQueryState(q))`
 * deep-equals `q` for every valid query state (Property 15: round-trip is the
 * identity).
 *
 * Implementation notes that make the round-trip exact:
 * - Array dimensions are emitted as repeated `URLSearchParams` entries (one per
 *   value) rather than a delimited string, so arbitrary values — including ones
 *   containing commas, `=`, `&`, or the empty string — survive verbatim and in
 *   order. `getAll` preserves both order and duplicates.
 * - An absent key decodes to the empty array `[]`; a single empty-string value
 *   decodes to `[""]`, so the two are never conflated.
 * - The search term and sort direction are always emitted, so `""` and the
 *   default direction round-trip unambiguously.
 * - The sort column key is emitted only when a column is selected; its absence
 *   decodes to `null`.
 * - On decode, every value is validated against its known set and unknown
 *   tokens are dropped, keeping the decoded shape within the type's domain.
 */

import type {
  CapabilityFilterMode,
  CapabilityKey,
  FilterState,
  QueryState,
  ScanStatus,
  SortColumn,
  SortDirection,
  SortState,
} from "../types";

// ---------------------------------------------------------------------------
// Parameter keys (kept short; emitted in a stable order)
// ---------------------------------------------------------------------------

const KEY_SEARCH = "q";
const KEY_STATUS = "status";
const KEY_LANGUAGE = "lang";
const KEY_VISIBILITY = "vis";
const KEY_PRIVATE = "priv";
const KEY_ARCHIVED = "arch";
const KEY_SORT_COLUMN = "sort";
const KEY_SORT_DIRECTION = "dir";
/** Capability keys are namespaced under a single prefix, e.g. `cap.hasCicd`. */
const CAPABILITY_PREFIX = "cap.";

// ---------------------------------------------------------------------------
// Known-value sets (used to validate/normalize on decode)
// ---------------------------------------------------------------------------

const SCAN_STATUSES: readonly ScanStatus[] = [
  "success",
  "partial",
  "failed",
];

const CAPABILITY_KEYS: readonly CapabilityKey[] = [
  "hasCicd",
  "hasDockerfile",
  "hasIac",
  "hasApiSpec",
  "hasReadme",
  "hasOwner",
  "hasDomainMatches",
  "hasErrors",
];

const CAPABILITY_MODES: readonly CapabilityFilterMode[] = [
  "present",
  "absent",
];

const SORT_COLUMNS: readonly SortColumn[] = [
  "repoName",
  "primaryLanguage",
  "size",
  "pushedAt",
  "status",
];

const SORT_DIRECTIONS: readonly SortDirection[] = ["asc", "desc"];

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/**
 * Serialize a {@link QueryState} into a URL query string (without a leading
 * `?`). The output is suitable for use as a `URLSearchParams`-style query
 * segment in the detail-navigation URL or preserved router state.
 */
export function encodeQueryState(q: QueryState): string {
  const params = new URLSearchParams();

  // Search term — always emitted so "" round-trips.
  params.set(KEY_SEARCH, q.searchTerm);

  // Attribute dimensions — repeated entries preserve order and duplicates.
  for (const status of q.filters.status) {
    params.append(KEY_STATUS, status);
  }
  for (const language of q.filters.language) {
    params.append(KEY_LANGUAGE, language);
  }
  for (const visibility of q.filters.visibility) {
    params.append(KEY_VISIBILITY, visibility);
  }
  for (const value of q.filters.private) {
    params.append(KEY_PRIVATE, encodeBoolean(value));
  }
  for (const value of q.filters.archived) {
    params.append(KEY_ARCHIVED, encodeBoolean(value));
  }

  // Capability filters — one entry per active capability, in canonical order.
  for (const capability of CAPABILITY_KEYS) {
    const mode = q.filters.capabilities[capability];
    if (mode !== undefined) {
      params.set(`${CAPABILITY_PREFIX}${capability}`, mode);
    }
  }

  // Sort — direction always emitted; column only when selected.
  if (q.sort.column !== null) {
    params.set(KEY_SORT_COLUMN, q.sort.column);
  }
  params.set(KEY_SORT_DIRECTION, q.sort.direction);

  return params.toString();
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

/**
 * Parse a query string produced by {@link encodeQueryState} back into a
 * {@link QueryState}. Tolerant of a leading `?`. Unknown or invalid tokens are
 * dropped so the result always stays within the type's domain; missing pieces
 * fall back to their inactive defaults (empty arrays, empty term, no column,
 * ascending direction).
 */
export function decodeQueryState(s: string): QueryState {
  const params = new URLSearchParams(s.startsWith("?") ? s.slice(1) : s);

  const searchTerm = params.get(KEY_SEARCH) ?? "";

  const filters: FilterState = {
    status: filterKnown(params.getAll(KEY_STATUS), SCAN_STATUSES),
    language: params.getAll(KEY_LANGUAGE),
    visibility: params.getAll(KEY_VISIBILITY),
    private: decodeBooleans(params.getAll(KEY_PRIVATE)),
    archived: decodeBooleans(params.getAll(KEY_ARCHIVED)),
    capabilities: decodeCapabilities(params),
  };

  const rawColumn = params.get(KEY_SORT_COLUMN);
  const column: SortColumn | null =
    rawColumn !== null && isOneOf(rawColumn, SORT_COLUMNS) ? rawColumn : null;

  const rawDirection = params.get(KEY_SORT_DIRECTION);
  const direction: SortDirection =
    rawDirection !== null && isOneOf(rawDirection, SORT_DIRECTIONS)
      ? rawDirection
      : "asc";

  const sort: SortState = { column, direction };

  return { searchTerm, filters, sort };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function encodeBoolean(value: boolean): string {
  return value ? "true" : "false";
}

/** Decode boolean tokens, preserving order and dropping anything unrecognized. */
function decodeBooleans(values: string[]): boolean[] {
  const result: boolean[] = [];
  for (const value of values) {
    if (value === "true") {
      result.push(true);
    } else if (value === "false") {
      result.push(false);
    }
  }
  return result;
}

/** Keep only the values that belong to the allowed set, preserving order. */
function filterKnown<T extends string>(
  values: string[],
  allowed: readonly T[],
): T[] {
  const result: T[] = [];
  for (const value of values) {
    if (isOneOf(value, allowed)) {
      result.push(value);
    }
  }
  return result;
}

function isOneOf<T extends string>(
  value: string,
  allowed: readonly T[],
): value is T {
  return (allowed as readonly string[]).includes(value);
}

function decodeCapabilities(
  params: URLSearchParams,
): Partial<Record<CapabilityKey, CapabilityFilterMode>> {
  const capabilities: Partial<Record<CapabilityKey, CapabilityFilterMode>> = {};
  for (const capability of CAPABILITY_KEYS) {
    const raw = params.get(`${CAPABILITY_PREFIX}${capability}`);
    if (raw !== null && isOneOf(raw, CAPABILITY_MODES)) {
      capabilities[capability] = raw;
    }
  }
  return capabilities;
}
